import { memo, useEffect, useState } from 'react'

import { Button, Group, Text } from '@mantine/core'

import {
  type KYCTokenOptionsType,
  useGetKycStatus,
} from '@repo/common/queries/kyc'
import type { KYCLevel } from '@repo/common/queries/token_auth'

import { LegalRestrictionContainer } from '@/components/LegalRestrictionContainer'
import { SumsubComponent } from '@/components/SumsubComponent'
import type { BasePanelProps } from '@/flows/flow_helpers'

export type ExtraIdentificationProps = {
  action_term: string
  level: KYCTokenOptionsType['level']
  allowed_kyc_levels: KYCLevel[]
}

export const PanelIdentification = memo<
  BasePanelProps & Omit<ExtraIdentificationProps, 'allowed_kyc_levels'>
>(function PanelIdentification({ action_term, level, onCompleted }) {
  const { data, isSuccess } = useGetKycStatus()
  const [local_applicant_type, set_applicant_type] = useState<
    KYCTokenOptionsType['applicant_type'] | undefined
  >(undefined)

  useEffect(() => {
    if (data?.passed) {
      onCompleted()
    }
  }, [data?.passed, onCompleted])

  if (!isSuccess) {
    return null
  }

  const applicant_type = data?.applicant_type ?? local_applicant_type

  if (applicant_type == null) {
    return (
      <PanelIndividualOrBusiness
        action_term={action_term}
        onAnswer={set_applicant_type}
      />
    )
  }

  return <SumsubComponent applicant_type={applicant_type} level={level} />
})

export const useSkipIdentification = ({
  allowed_kyc_levels,
}: ExtraIdentificationProps) => {
  const { data } = useGetKycStatus()

  return (
    Boolean(data?.passed) && allowed_kyc_levels.includes(data?.level ?? 'none')
  )
}

const PanelIndividualOrBusiness = memo<{
  action_term: string
  onAnswer: (arg: KYCTokenOptionsType['applicant_type']) => void
}>(
  // eslint-disable-next-line max-lines-per-function, complexity
  function PanelIndividualOrBusiness({ action_term, onAnswer }) {
    return (
      <LegalRestrictionContainer>
        <Text size="lg"> Are you {action_term} as a </Text>
        <Group>
          <Button
            variant="filled"
            size="lg"
            flex="1 1"
            onClick={() => onAnswer('individual')}
          >
            Individual
          </Button>
          <Button
            variant="filled"
            size="lg"
            flex="1 1"
            onClick={() => onAnswer('business')}
          >
            Business
          </Button>
        </Group>
      </LegalRestrictionContainer>
    )
  },
)
